import { Capacitor } from '@capacitor/core'
import { FirebaseError } from 'firebase/app'
import {
  browserLocalPersistence,
  browserSessionPersistence,
  confirmPasswordReset,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithCustomToken,
  signOut,
  User,
  verifyPasswordResetCode
} from 'firebase/auth'
import { useEffect, useState } from 'react'

import { useForgotPassword, useForgotPasswordByUsername, useLogin } from '@apps-common/apis/auth'
import { queryClient } from '@apps-common/lib'
import { IAuthActionResponse } from '@apps-common/types/auth'
import { AuthenticatedUser } from '@apps-common/types/portal'

// const ERROR_CODES = ['auth/user-not-found', 'auth/wrong-password', 'auth/expired-action-code']
const AUTH_INITIALIZATION_DELAY_MS = 500

const useFirebaseAuth = () => {
  const [initialized, setInitialized] = useState(false)
  const [user, setUser] = useState<AuthenticatedUser | null>(null)

  const firebaseAuth = getAuth()
  const { mutateAsync: mutateLoginAsync } = useLogin()
  const { mutateAsync: mutateForgotPasswordAsync } = useForgotPassword()
  const { mutateAsync: mutateForgotPasswordByUsernameAsync } = useForgotPasswordByUsername()

  const createUser = async (firebaseUser: User) => {
    const idToken = await firebaseUser.getIdToken()
    const idTokenResult = await firebaseUser.getIdTokenResult()

    return {
      ...firebaseUser,
      idToken,
      idTokenResult
    }
  }

  const updateProfileImage = (newPhotoUrl: string) => {
    if (user) {
      setUser((old) => (old ? { ...old, photoURL: newPhotoUrl } : old))
    }
  }

  const reloadUser = async () => {
    await firebaseAuth?.currentUser?.reload();

    const updatedUser = firebaseAuth.currentUser


    if (updatedUser && user) {
      setUser({
        ...updatedUser,
        idTokenResult: user?.idTokenResult,
        idToken: user?.idToken
      })
    }
  }

  const updateUser = (value: AuthenticatedUser | null) => {
    setInitialized(false)
    setUser(value)
    setTimeout(() => {
      setInitialized(true)
    }, AUTH_INITIALIZATION_DELAY_MS)
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      firebaseAuth,
      (firebaseUser) => {
        if (firebaseUser) {
          // console.log('IN FIREBASE AUTH --> ON STATE CHANGED')

          createUser(firebaseUser).then((value) => {
            updateUser(value)
          })
        } else {
          updateUser(null)
        }
      },
      (error) => { }
    )

    return () => unsubscribe()
  }, [firebaseAuth])

  const logIn = async (email: string, password: string, persist: boolean): Promise<AuthenticatedUser | string> => {
    if (!Capacitor.isNativePlatform()) {
      setPersistence(firebaseAuth, persist ? browserLocalPersistence : browserSessionPersistence)
    }

    try {
      const tokenRes = await mutateLoginAsync({ model: { email, password } })

      const res = await signInWithCustomToken(firebaseAuth, tokenRes.token)

      const u = await createUser(res.user)

      updateUser(u)

      return u
    } catch (error: any) {
      console.log(error)
      return error?.message || 'Login failed.'
    }
  }

  const logOut = () => {
    console.log('LOGGING OUT OF FIREBASE...')
    signOut(firebaseAuth).then(() => {
      updateUser(null)
      queryClient.cancelQueries()
    })
  }

  const forgotPassword = async (email: string): Promise<IAuthActionResponse> => {
    let message: string = 'Failed to send email.'

    try {
      // send reset password request to api
      const res = await mutateForgotPasswordAsync({ model: { email } })
      console.log('res', res)
      return res
      // console.log(res)
    } catch (e: any) {
      // do nothing
      // all fall through
      console.log(e)
      if (e?.message) {
        message = e?.message
      }
    }

    return { result: false, message }
  }

  const forgotPasswordByUsername = async (username: string): Promise<IAuthActionResponse> => {
    let message: string = 'Failed to send email.'

    try {
      // send reset password request to api
      const res = await mutateForgotPasswordByUsernameAsync({ model: { username } })
      console.log('res', res)
      return res
      // console.log(res)
    } catch (e: any) {
      // do nothing
      // all fall through
      console.log(e)
      if (e?.message) {
        message = e?.message
      }
    }

    return { result: false, message }
  }

  const resetPassword = async (email: string, code: string, newPassword: string): Promise<IAuthActionResponse> => {
    try {
      await confirmPasswordReset(firebaseAuth, code, newPassword)
      const res = await logIn(email, newPassword, true)

      // is result is a string, then it's an error
      if (typeof res === 'string') {
        return { result: false, message: 'Your password has been updated, but login failed. Return to login and try again.' }
      }

      return { result: true, message: 'Password changed successfully.' }
    } catch {
      return { result: false, message: 'Password reset failed.' }
    }
  }

  const verifyCode = async (code: string): Promise<IAuthActionResponse> => {
    let email: string | null = null

    console.log('Verifying code...')
    try {
      email = await verifyPasswordResetCode(firebaseAuth, code)
    } catch (e) {
      if (e instanceof FirebaseError) {
        // check error codes
        console.log(e.code)
      } else {
        console.log(e)
      }

      // do nothing
      // allow fall through
    }

    return { result: !!email, message: email || '' }
  }

  return {
    initialized,
    user,
    logIn,
    logOut,
    forgotPassword,
    forgotPasswordByUsername,
    resetPassword,
    verifyCode,
    reloadUser,
    updateProfileImage
  }
}

export default useFirebaseAuth
